import { defineStore } from "pinia"
import type { IconType } from "~/types/base-icons"

export type PanelMenuItemIds =
	| "dashboard"
	| "manage-users"
	| "manage-payrolls"
	| "manage-reward-payrolls"
	| "manage-performance-reviews"
	| "manage-careers"
	| "manage-career-requests"
	| "manage-categories"
	| "manage-custom-pages"
	| "manage-attendances"
	| "manage-company-settings"
	| string

export type PanelMenuItem = {
	id: PanelMenuItemIds
	title: string
	icon?: IconType
	iconify?: string
	to: string
	color: string
	yellow?: boolean
	roles: (string | null)[]
	company: boolean | null
}

export type CompanyPanelMenuItemIds =
	| "dashboard"
	| "manage-payrolls"
	| "manage-reward-payrolls"
	| "manage-performance-reviews"
	| string

export type CompanyPanelMenuItem = {
	id: CompanyPanelMenuItemIds
	title: string
	icon?: IconType
	iconify?: string
	to: string
	color?: string
	yellow?: boolean
	new?: number
	roles?: string[]
	company?: boolean | null
}

export type AdminMenuItemIds =
	| "dashboard"
	| "manage-slides"
	| "manage-users"
	| "manage-categories"
	| "manage-rules"
	| "manage-contact-us"
	| "manage-about-us"
	| "manage-logs"
	| "manage-metas"
	| "manage-dev-tickets"
	| "show-website"
	| "article"

export type AdminMenuItem = {
	id: AdminMenuItemIds
	title: string
	icon: string
	to: string
	color: string
	yellow?: boolean
	roles: string[]
}

export const usePanelStore = defineStore("panel", () => {
	const { $i18n, $routeUrl } = useNuxtApp()
	const userStore = useUserStore()
	const companyStore = useCompanyStore()

	const state = {
		adminMenuItems: ref<AdminMenuItem[]>([
			{
				id: "dashboard",
				title: $i18n.t("dashboard"),
				icon: "mdi:view-dashboard",
				to: $routeUrl.AdminDashboardUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			},
			{
				id: "manage-users",
				title: $i18n.t("users"),
				icon: "mdi:account-supervisor",
				to: $routeUrl.ManageUsersUrl(),
				color: "#455a64",
				roles: ["admin", "support"]
			},
			{
				id: "manage-slides",
				title: $i18n.t("slides"),
				icon: "mdi:folder-image",
				to: $routeUrl.ManageSlidesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-categories",
				title: $i18n.t("categories"),
				icon: "mdi:format-list-bulleted-square",
				to: $routeUrl.ManageCategoriesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "article",
				title: $i18n.t("articles"),
				icon: "mdi:post",
				to: $routeUrl.ManagePostsUrl("article"),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-rules",
				title: $i18n.t("rules"),
				icon: "mdi:gavel",
				to: $routeUrl.ManageRulesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-about-us",
				title: $i18n.t("about_us"),
				icon: "mdi:card-account-phone",
				to: $routeUrl.ManageAboutUsUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-contact-us",
				title: $i18n.t("contact_us"),
				icon: "mdi:card-account-phone",
				to: $routeUrl.ManageContactUsUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-logs",
				title: $i18n.t("logs"),
				icon: "mdi:history",
				to: $routeUrl.ManageLogsUrl(),
				color: "#455a64",
				roles: ["admin"]
			},
			{
				id: "manage-metas",
				title: $i18n.t("metas"),
				icon: "mdi:application-cog",
				to: $routeUrl.ManageMetasUrl(),
				color: "#455a64",
				roles: ["admin"]
			},
			{
				id: "manage-dev-tickets",
				title: $i18n.t("technical_tickets"),
				icon: "mdi:lightbulb-question",
				to: $routeUrl.ManageDevTicketsUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			},
			{
				id: "show-website",
				title: $i18n.t("show_website"),
				icon: "mdi:eye",
				to: $routeUrl.HomeUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			}
		]),
		langs: ref<string[]>(["fa"])
	}

	const panelMenuItems = computed<PanelMenuItem[]>(() => [
		{
			id: "dashboard",
			title: $i18n.t("dashboard"),
			icon: "grid",
			to: $routeUrl.PanelDashboardUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.role_id ? "company-user" : null
			].filter((v) => v),
			company: null
		},
		{
			id: "manage-users",
			title: $i18n.t("users"),
			icon: "users",
			to: $routeUrl.PanelUsersUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_user_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-projects",
			title: $i18n.t("projects"),
			iconify: "si:projects-fill",
			to: $routeUrl.PanelProjectsUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_project_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-payrolls",
			title: $i18n.t("payrolls"),
			icon: "money",
			to: $routeUrl.PanelPayrollsUrl("salary"),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_payroll_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-reward-payrolls",
			title: $i18n.t("payroll_rewards"),
			icon: "money",
			to: $routeUrl.PanelPayrollsUrl("reward"),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_payroll_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-performance-reviews",
			title: $i18n.t("performance_reviews"),
			icon: "positive-review",
			to: $routeUrl.PanelPerformanceReviewsUrl("normal"),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_performance_review_show || userStore.user?.allow_full_access
					? "company-user"
					: null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-categories",
			title: $i18n.t("categories"),
			iconify: "mdi:format-list-bulleted-square",
			to: $routeUrl.PanelCategoriesUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_category_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-careers",
			title: $i18n.t("careers"),
			icon: "work",
			to: $routeUrl.PanelCareersUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_career_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-career-requests",
			title: $i18n.t("career_requests"),
			icon: "work",
			to: $routeUrl.PanelCareerRequestsUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_career_request_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-attendances",
			title: $i18n.t("attendances"),
			iconify: "mdi:face-recognition",
			to: $routeUrl.PanelAttendancesUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_attendance_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-disciplinary-notes",
			title: $i18n.t("disciplinary_notes"),
			iconify: "material-symbols:clinical-notes-rounded",
			to: $routeUrl.PanelDisciplinaryNotesUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_disciplinary_note_show || userStore.user?.allow_full_access
					? "company-user"
					: null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-announcements",
			title: $i18n.t("announcements"),
			iconify: "mdi:announcement",
			to: $routeUrl.PanelAnnouncementsUrl(),
			color: "#455a64",
			roles: ["admin", "support", "blogger", "developer", "user", "company-user"].filter((v) => v),
			company: true
		},
		{
			id: "manage-organization-chart",
			title: $i18n.t("organization_chart"),
			iconify: "mdi:family-tree",
			to: $routeUrl.PanelOrganizationChartUrl(),
			color: "#455a64",
			roles: ["admin", "support", "blogger", "developer", "user", "company-user"].filter((v) => v),
			company: true
		},
		{
			id: "manage-custom-pages",
			title: $i18n.t("custom_pages"),
			iconify: "ooui:special-pages-rtl",
			to: $routeUrl.PanelCustomPagesUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_career_request_show || userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		},
		{
			id: "manage-company-settings",
			title: $i18n.t("settings"),
			iconify: "mdi:settings",
			to: $routeUrl.PanelCompanySettingsUrl(),
			color: "#455a64",
			roles: [
				"admin",
				"support",
				"blogger",
				"developer",
				"user",
				userStore.user?.allow_full_access ? "company-user" : null
			].filter((v) => v),
			company: true
		}
	])

	const companyPanelItems = computed<CompanyPanelMenuItem[]>(() =>
		[
			{
				id: "dashboard",
				title: $i18n.t("dashboard"),
				icon: "grid",
				to: $routeUrl.CompanyDashboardUrl()
			},
			{
				id: "manage-payrolls",
				title: $i18n.t("payrolls"),
				icon: "money",
				to: $routeUrl.CompanyPayrollsUrl("salary"),
				new: companyStore.company?.total_unseen_payrolls || 0
			},
			{
				id: "manage-reward-payrolls",
				title: $i18n.t("payroll_rewards"),
				icon: "money",
				to: $routeUrl.CompanyPayrollsUrl("reward"),
				new: companyStore.company?.total_unseen_reward_payrolls || 0
			},
			{
				id: "manage-performance-reviews",
				title: $i18n.t("performance_reviews"),
				icon: "positive-review",
				to: $routeUrl.CompanyPerformanceReviewsUrl("normal"),
				color: "#455a64",
				company: true,
				new: companyStore.company?.total_unseen_performance_reviews || 0
			},
			{
				id: "manage-disciplinary-notes",
				title: $i18n.t("disciplinary_notes"),
				iconify: "material-symbols:clinical-notes-rounded",
				to: $routeUrl.CompanyDisciplinaryNotesUrl(),
				color: "#455a64",
				company: true,
				new: companyStore.company?.total_unseen_disciplinary_notes || 0
			},
			{
				id: "manage-announcements",
				title: $i18n.t("announcements"),
				iconify: "mdi:announcement",
				to: $routeUrl.CompanyAnnouncementsUrl(),
				color: "#455a64",
				company: true,
				new: companyStore.company?.total_unseen_announcements || 0
			},
			companyStore.company?.has_project_access
				? {
						id: "manage-projects",
						title: $i18n.t("projects"),
						iconify: "si:projects-fill",
						to: $routeUrl.CompanyProjectsUrl(),
						color: "#455a64",
						company: true
					}
				: (null as any),
			{
				id: "manage-attendances",
				title: $i18n.t("attendances"),
				iconify: "mdi:face-recognition",
				to: $routeUrl.CompanyAttendancesUrl(),
				color: "#455a64",
				company: true
			}
		].filter((v) => v)
	)

	return {
		...state,
		panelMenuItems,
		companyPanelItems
	}
})
